'use client';

import { useState, useEffect, useId } from 'react';
import styles from './checkbox.module.css';

export default function Checkbox({
  type = 'checkbox', // "checkbox" or "radio"
  checked = false,
  onChange,
  label,
  name,
  value,
  disabled = false,
  className = '',
  id,
  required = false,
  backgroundColor = '#444CE7', // Default background color for checked state
  ...props
}) {
  const [isChecked, setIsChecked] = useState(checked);
  const generatedId = useId();
  const uniqueId = id || generatedId;

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  const handleClick = () => {
    if (disabled) return;

    if (type === 'checkbox') {
      // For checkboxes, toggle the state
      const newValue = !isChecked;
      setIsChecked(newValue);

      if (onChange) {
        onChange(newValue);
      }
    } else {
      // For radio buttons, always set to true (can't deselect)
      if (!isChecked) {
        setIsChecked(true);

        if (onChange) {
          onChange(value);
        }
      }
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const handleInputChange = e => {
    if (disabled) return;
    const newChecked = e.target.checked;
    setIsChecked(newChecked);
    if (onChange) {
      onChange(type === 'checkbox' ? newChecked : value);
    }
  };

  const LabelWrapper = label ? 'label' : 'div';

  return (
    <LabelWrapper
      className={`${styles.container} ${className} ${disabled ? styles.disabled : ''}`}
      onClick={!label ? handleClick : undefined}
      onKeyDown={handleKeyDown}
      tabIndex={label ? undefined : disabled ? -1 : 0}
      role={label ? undefined : type}
      aria-checked={label ? undefined : isChecked}
      aria-disabled={label ? undefined : disabled}
      aria-required={label ? undefined : required}
      htmlFor={label ? uniqueId : undefined}
      {...(label ? {} : props)}
    >
      <div
        className={`${styles.checkbox} ${isChecked ? styles.checked : ''} ${type === 'radio' ? styles.radio : ''}`}
      >
        <input
          type={type}
          id={uniqueId}
          name={name}
          value={value}
          checked={isChecked}
          onChange={handleInputChange}
          disabled={disabled}
          required={required}
          className={styles.hiddenInput}
          suppressHydrationWarning
        />

        {type === 'checkbox' ? (
          <div className={styles.checkboxInner}>
            {isChecked ? (
              <svg
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M0 6C0 2.68629 2.68629 0 6 0H14C17.3137 0 20 2.68629 20 6V14C20 17.3137 17.3137 20 14 20H6C2.68629 20 0 17.3137 0 14V6Z'
                  fill={backgroundColor}
                />
                <path
                  d='M14.6668 6.5L8.25016 12.9167L5.3335 10'
                  stroke='white'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='#fff'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M6 0.5H14C17.0376 0.5 19.5 2.96243 19.5 6V14C19.5 17.0376 17.0376 19.5 14 19.5H6C2.96243 19.5 0.5 17.0376 0.5 14V6C0.5 2.96243 2.96243 0.5 6 0.5Z'
                  stroke='#E3E8EF'
                />
              </svg>
            )}
          </div>
        ) : (
          <div className={styles.radioInner}>
            {isChecked ? (
              <svg
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10Z'
                  fill={backgroundColor}
                />
                <path
                  d='M6 10C6 7.79086 7.79086 6 10 6C12.2091 6 14 7.79086 14 10C14 12.2091 12.2091 14 10 14C7.79086 14 6 12.2091 6 10Z'
                  fill='white'
                />
              </svg>
            ) : (
              <svg
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M10 0.5C15.2467 0.5 19.5 4.7533 19.5 10C19.5 15.2467 15.2467 19.5 10 19.5C4.7533 19.5 0.5 15.2467 0.5 10C0.5 4.7533 4.7533 0.5 10 0.5Z'
                  stroke='#E3E8EF'
                />
              </svg>
            )}
          </div>
        )}
      </div>

      {label ? (
        <span className={styles.label}>{label}</span>
      ) : null}
    </LabelWrapper>
  );
}
